"use client";

import { ReactNode } from "react";
import { HydrationBoundary, useQueryClient } from "@tanstack/react-query";
import type { DehydratedState } from "@tanstack/react-query";

type MaybeInfinite = { pages?: unknown[] };

function pageCount(data: unknown): number | null {
  const pages = (data as MaybeInfinite | undefined)?.pages;
  return Array.isArray(pages) ? pages.length : null;
}

/**
 * `HydrationBoundary` that never shrinks an infinite query.
 *
 * A Server Action's response always carries a fresh RSC render of the current
 * route, so the server component re-prefetches page 1 and re-emits a dehydrated
 * state with a newer `dataUpdatedAt`. Hydration only overwrites when the
 * incoming data is newer, so that page-1 snapshot replaces a client cache
 * holding pages 1..N — the grid collapses to 20 items, the browser clamps
 * scrollTop to the shorter content, and the scroll position is lost.
 *
 * Queries are dropped from the hydration only when the client already holds
 * MORE pages than the server is offering. Everything else hydrates as normal:
 * plain queries, first hydration, new query hashes from filter changes, and
 * infinite queries the user hasn't scrolled past page 1 — so fresh server data
 * still lands in all the cases where it can't destroy anything.
 */
export function HydrateKeepingPages({
  state,
  children,
}: {
  state: DehydratedState;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();

  const queries = state.queries.filter((dehydrated) => {
    const incoming = pageCount(dehydrated.state.data);
    if (incoming === null) return true;
    const existing = pageCount(cache.get(dehydrated.queryHash)?.state.data);
    return existing === null || existing <= incoming;
  });

  return (
    <HydrationBoundary state={{ ...state, queries }}>
      {children}
    </HydrationBoundary>
  );
}
